// The counter poster on the dashboard (issue #13, US-6): the secondary language picker inside the settings form, and
// the A4 / A5 download buttons of a pickup point (on its dashboard card and on the settings page). Labels are the
// language names in their own script, never flags; each download state carries a symbol and words, never color alone.
import { useState, type ReactNode } from 'react';
import { LANG_LABELS, POSTER_SIZES, POSTER_SIZES_LIST, UI, fill, posterFileName, posterSecondaryOptions, type Lang, type PosterSize } from '@ringring/core';
import type { ApiError } from './api';

/** The secondary language the form shows as chosen: the owner's choice while it is still offered, else the first option. */
export function effectivePosterLang2(defaultLang: Lang, languages: readonly Lang[], chosen: Lang | null): Lang | null {
  const options = posterSecondaryOptions(defaultLang, languages);
  return chosen && options.includes(chosen) ? chosen : (options[0] ?? null);
}

/**
 * The poster card of the settings form: radio buttons for the second language among the venue's other languages (a
 * hint when there is none), then `children` (the download buttons of each pickup point).
 */
export function PosterSettings({ lang, defaultLang, languages, value, onChange, children }: { lang: Lang; defaultLang: Lang; languages: readonly Lang[]; value: Lang | null; onChange: (l: Lang) => void; children?: ReactNode }) {
  const t = UI[lang];
  const options = posterSecondaryOptions(defaultLang, languages);
  const current = effectivePosterLang2(defaultLang, languages, value);
  return (
    <section className="card" aria-labelledby="s-poster">
      <h2 id="s-poster">{t.poster_section}</h2>
      <p className="muted">{t.poster_intro}</p>
      {options.length === 0 ? (
        <p className="muted" role="status">
          <span aria-hidden="true">ℹ</span> {t.poster_one_lang}
        </p>
      ) : (
        <>
          <fieldset className="choices inline">
            <legend>{t.poster_lang_2}</legend>
            {options.map((l) => (
              <label className="check" key={l} lang={l}>
                <input type="radio" name="poster_lang_2" value={l} checked={current === l} onChange={() => onChange(l)} />
                <span>{LANG_LABELS[l]}</span>
              </label>
            ))}
          </fieldset>
          <p className="muted">{fill(t.poster_lang_hint, { lang: LANG_LABELS[defaultLang] })}</p>
        </>
      )}
      {children}
    </section>
  );
}

type DownloadState = { size: PosterSize; status: 'busy' | 'done' | 'failed' } | null;

const STATUS: Record<'busy' | 'done' | 'failed', { symbol: string; key: 'poster_downloading' | 'poster_downloaded' | 'poster_failed'; tag: string }> = {
  busy: { symbol: '…', key: 'poster_downloading', tag: '' },
  done: { symbol: '✓', key: 'poster_downloaded', tag: 'ready' },
  failed: { symbol: '✕', key: 'poster_failed', tag: 'call' },
};

export interface PosterDownloadsProps {
  lang: Lang;
  venueName: string;
  pp: { id: string; name: string };
  /** Fetches and saves the PDF; throws the ApiError of a refusal. */
  onDownload: (size: PosterSize, fileName: string) => Promise<void>;
  /** Shown under the buttons; the settings page reminds that the poster uses the saved settings. */
  note?: string;
  /** Test seam: the state a render starts in. */
  initial?: DownloadState;
}

/** One 48 px button per size, "Télécharger l'affiche A4" / "A5", and the state of the last download. */
export function PosterDownloads({ lang, venueName, pp, onDownload, note, initial = null }: PosterDownloadsProps) {
  const t = UI[lang];
  const [state, setState] = useState<DownloadState>(initial);
  const download = async (size: PosterSize) => {
    setState({ size, status: 'busy' });
    try {
      await onDownload(size, posterFileName(venueName, pp, size));
      setState({ size, status: 'done' });
    } catch (e) {
      if ((e as ApiError).status === 401) {
        window.location.hash = '#/signin';
        return;
      }
      setState({ size, status: 'failed' });
    }
  };
  const s = state ? STATUS[state.status] : null;
  return (
    <div className="poster-downloads">
      <div className="row">
        {POSTER_SIZES_LIST.map((size) => (
          <button key={size} className="btn secondary" type="button" disabled={state?.status === 'busy'} onClick={() => void download(size)} aria-describedby={note ? `poster-note-${pp.id}` : undefined}>
            {fill(t.poster_download, { size: POSTER_SIZES[size].label })}
          </button>
        ))}
        {state && s ? (
          <span className={`tag ${s.tag}`.trim()} role="status" aria-live="polite">
            <span aria-hidden="true">{s.symbol}</span> {t[s.key]} · <bdi dir="ltr">{POSTER_SIZES[state.size].label}</bdi>
          </span>
        ) : null}
      </div>
      {note ? (
        <p id={`poster-note-${pp.id}`} className="muted">
          {note}
        </p>
      ) : null}
    </div>
  );
}
